/**
 * NewShiftPage  —  /report/:clinicId/schedule/new-shift
 *
 * Three-tab schedule-entry tool:
 *   1. Single employee  — browse staff and view their current weekly schedule indicators.
 *   2. Bulk / week template — select or create a named shift template and apply it to all
 *      matching practitioners in one parallel batch.
 *   3. Copy schedule  — copy an existing week's schedule to another week, or create a
 *      quick single-day override for one practitioner.
 *
 * API endpoints / tables:
 *   GET  /api/practitioners/?clinic=<id>
 *        → reads `practitioners` + `practitioner_profile_PractitionerProfile`
 *
 *   GET  /api/practitioner-weekly-schedules/?clinic=<id>
 *        → reads `practitioner_weekly_schedule`; the base row is displayed as
 *          filled/ghost day indicators
 *
 *   POST/PATCH /api/practitioner-weekly-schedules/   (Bulk apply, single-employee save)
 *        → writes `practitioner_weekly_schedule`, including per-day `breaks`
 *          ([{start, end}] inside each day slot). Leaving "Effective from/until" blank
 *          writes the practitioner's base row (the recurring default). Setting both
 *          writes a date-bounded row that overrides the base row inside that range only.
 *
 *   POST /api/practitioner-schedule-overrides/    (Quick override)
 *        → writes to `practitioner_profile_PractitionerScheduleOverride`
 *        → fields: profile, date_ranges ([{start_date,end_date}]), is_open, start_time, end_time, reason
 *
 * Custom template persistence:
 *   User-created templates are saved to localStorage under the key defined by TEMPLATES_KEY.
 *   Default templates are hardcoded — they are never written to the database.
 */
import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  usePractitioners,
  usePractitionerWeeklySchedules,
  useSavePractitionerWeeklySchedule,
  useCreatePractitionerScheduleOverride,
  useCopySchedule,
} from "@api/hooks";
import {
  baseSchedule,
  groupSchedulesByPractitioner,
  compactSchedule,
  slotBreaks,
  slotHours,
  breakError,
  scheduleBreakError,
} from "@utils/weeklySchedule";
import { getRoster, getInitials } from "@utils/practitionerUtils"; // getInitials moved from local to shared
import { normalizeTypeForUi, safeText } from "@utils/utils";
import { TYPE_LABELS, TYPE_LABELS_PLURAL, TYPE_ORDER, typeColor } from "@utils/practitionerTypes"; // replaces 70+ lines of local TYPE_LABELS/TYPE_COLORS/TYPE_ORDER maps
import { SCHEDULE_TIMES, extractApiError } from "@common/scheduleUtils";
import { Button, Card, CheckboxField, showSuccess, showError } from "@common";

// M T W T F S S in schedule key order
const DAY_INDICATORS = [
  { key: "monday", letter: "M" },
  { key: "tuesday", letter: "T" },
  { key: "wednesday", letter: "W" },
  { key: "thursday", letter: "T" },
  { key: "friday", letter: "F" },
  { key: "saturday", letter: "S" },
  { key: "sunday", letter: "S" },
];

// Pill button with an inset hairline ring, per the requested style.
const BREAK_BTN_CLS =
  "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition-all " +
  "text-palette-gray-secondary shadow-[inset_0_0_0_1px_oklch(0.9_0.005_250)] " +
  "hover:text-palette-blue-heading";

// ── Tab icons ──────────────────────────────────────────────────────────────────

function IconSingleEmployee() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function IconBulk() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function IconCopy() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

// ── Bulk / week template ───────────────────────────────────────────────────────

const DEFAULT_TEMPLATES = [
  {
    id: "standard-mon-fri",
    name: "Standard Mon–Fri",
    days: ["monday", "tuesday", "wednesday", "thursday", "friday"],
    start: "8:00 AM",
    end: "5:00 PM",
  },
  {
    id: "morning-shift",
    name: "Morning shift",
    days: ["monday", "tuesday", "wednesday", "thursday", "friday"],
    start: "7:00 AM",
    end: "1:00 PM",
  },
  {
    id: "afternoon-late",
    name: "Afternoon / late",
    days: ["monday", "tuesday", "wednesday", "thursday", "friday"],
    start: "12:00 PM",
    end: "8:00 PM",
  },
  {
    id: "weekend-coverage",
    name: "Weekend coverage",
    days: ["saturday", "sunday"],
    start: "9:00 AM",
    end: "3:00 PM",
  },
];

const DAY_BUTTONS = [
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
  { key: "sunday", label: "Sun" },
];

const DAY_SHORT = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

function templateSummary(t) {
  return `${t.days.map((d) => DAY_SHORT[d]).join(", ")} · ${t.start} – ${t.end}`;
}

// ── localStorage persistence for user-created templates ────────────────────────

const TEMPLATES_KEY = "dentaparse_custom_templates";

function readCustomTemplates() {
  try {
    return JSON.parse(localStorage.getItem(TEMPLATES_KEY) || "[]");
  } catch {
    return [];
  }
}

function writeCustomTemplates(list) {
  try {
    localStorage.setItem(TEMPLATES_KEY, JSON.stringify(list));
  } catch {
    /* ignore — private browsing, quota exceeded, etc. */
  }
}

function BulkWeekTemplateTab({ roster, clinicId }) {
  // customTemplates = only user-created ones (persisted); DEFAULT_TEMPLATES are always hardcoded
  const [customTemplates, setCustomTemplates] = useState(readCustomTemplates);
  const templates = useMemo(() => [...DEFAULT_TEMPLATES, ...customTemplates], [customTemplates]);
  const [selectedId, setSelectedId] = useState(null);
  const [roleFilter, setRoleFilter] = useState("all");
  const [weekStarting, setWeekStarting] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay() + 1); // snap to Monday
    return d.toISOString().split("T")[0];
  });
  const [applying, setApplying] = useState(false);
  const [applyMsg, setApplyMsg] = useState("");

  // Create new template form state
  const [newName, setNewName] = useState("");
  const [newStart, setNewStart] = useState("8:00 AM");
  const [newEnd, setNewEnd] = useState("5:00 PM");
  const [newDays, setNewDays] = useState(["monday", "tuesday", "wednesday", "thursday", "friday"]);

  const selectedTemplate = templates.find((t) => t.id === selectedId) ?? null;

  const saveScheduleMut = useSavePractitionerWeeklySchedule();
  const { data: weeklyScheduleData } = usePractitionerWeeklySchedules({ clinic: clinicId });
  const schedulesByPractitioner = useMemo(
    () => groupSchedulesByPractitioner(weeklyScheduleData),
    [weeklyScheduleData],
  );

  const targetRoster = useMemo(() => {
    return roster.filter((p) => {
      if (roleFilter === "all") return true;
      const type = normalizeTypeForUi(p?.practitioner_type);
      return type === roleFilter.toUpperCase();
    });
  }, [roster, roleFilter]);

  async function handleApply() {
    if (!selectedTemplate) return;
    setApplying(true);
    setApplyMsg("");

    const weeklySchedule = {};
    for (const day of selectedTemplate.days) {
      weeklySchedule[day] = { start: selectedTemplate.start, end: selectedTemplate.end };
    }

    // Fire all writes in parallel instead of sequentially so the whole batch
    // completes in ~1 round-trip regardless of roster size. A template is the
    // recurring default, so this writes each practitioner's base row.
    const results = await Promise.allSettled(
      targetRoster.map((p) =>
        saveScheduleMut.mutateAsync({
          practitionerId: p.id,
          schedule: weeklySchedule,
          existingRows: schedulesByPractitioner[p.id] ?? [],
        }),
      ),
    );

    const ok = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.filter((r) => r.status === "rejected");
    setApplying(false);
    setApplyMsg(`Applied to ${ok} of ${targetRoster.length} staff.`);
    if (ok > 0) showSuccess(`Schedule applied to ${ok} of ${targetRoster.length} staff.`);
    // "Applied to 3 of 5" alongside a green toast reads as success. The rejected
    // ones are usually clashes with the same person's hours at another clinic —
    // named here, because nothing else on the page says which ones failed.
    if (failed.length > 0) {
      showError(
        `${failed.length} of ${targetRoster.length} could not be saved — ` +
          extractApiError(failed[0].reason, "check for schedule conflicts."),
      );
    }
  }

  function handleSaveTemplate() {
    if (!newName.trim() || newDays.length === 0) return;
    const newTemplate = {
      id: `custom-${Date.now()}`,
      name: newName.trim(),
      days: [...newDays],
      start: newStart,
      end: newEnd,
    };
    const updated = [...customTemplates, newTemplate];
    setCustomTemplates(updated);
    writeCustomTemplates(updated);
    showSuccess("Template saved.");
    setNewName("");
    setNewStart("8:00 AM");
    setNewEnd("5:00 PM");
    setNewDays(["monday", "tuesday", "wednesday", "thursday", "friday"]);
  }

  function toggleNewDay(key) {
    setNewDays((prev) => (prev.includes(key) ? prev.filter((d) => d !== key) : [...prev, key]));
  }

  return (
    <div className="space-y-5">
      {/* ── Week templates ──────────────────────────────────────────────────── */}
      <Card className="shadow-sm border border-palette-gray-subtle" bodyClassName="p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <div className="w-8 h-8 rounded-xl bg-palette-blue-muted flex items-center justify-center shrink-0">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              className="text-palette-indigo-badge"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="7" height="7" />
              <rect x="14" y="3" width="7" height="7" />
              <rect x="3" y="14" width="7" height="7" />
              <rect x="14" y="14" width="7" height="7" />
            </svg>
          </div>
          <h2 className="text-base font-semibold text-palette-slate-text">Week templates</h2>
        </div>

        {/* Template cards */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {templates.map((tmpl) => (
            <button
              key={tmpl.id}
              type="button"
              onClick={() => setSelectedId((prev) => (prev === tmpl.id ? null : tmpl.id))}
              className={`text-left p-4 rounded-xl border transition-all ${
                selectedId === tmpl.id
                  ? "border-palette-blue-label bg-palette-blue-soft ring-2 ring-palette-blue-border"
                  : "border-palette-gray-divider bg-white hover:border-palette-gray-border hover:bg-palette-gray-soft"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  className={
                    selectedId === tmpl.id
                      ? "text-palette-indigo-badge"
                      : "text-palette-slate-placeholder"
                  }
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                </svg>
                <p
                  className={`text-sm font-semibold ${selectedId === tmpl.id ? "text-palette-blue-heading" : "text-palette-slate-text"}`}
                >
                  {tmpl.name}
                </p>
              </div>
              <p className="text-xs text-palette-gray-secondary pl-5">{templateSummary(tmpl)}</p>
            </button>
          ))}
        </div>

        {/* Controls */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-xs font-semibold text-palette-gray-secondary uppercase tracking-widest mb-2 block">
              Apply to role(s)
            </label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full rounded-xl border border-palette-gray-divider px-4 py-2.5 text-sm text-palette-slate-heading bg-white outline-none focus:ring-2 focus:ring-palette-blue-ring"
            >
              <option value="all">All roles</option>
              {Object.entries(TYPE_LABELS_PLURAL).map(([code, label]) => (
                <option key={code} value={code.toLowerCase()}>
                  {label} only
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-palette-gray-secondary uppercase tracking-widest mb-2 block">
              Week starting
            </label>
            <input
              type="date"
              value={weekStarting}
              onChange={(e) => setWeekStarting(e.target.value)}
              className="w-full rounded-xl border border-palette-gray-divider px-4 py-2.5 text-sm text-palette-slate-heading outline-none focus:ring-2 focus:ring-palette-blue-ring"
            />
          </div>
        </div>

        {!selectedTemplate && !applyMsg && (
          <p className="text-xs text-palette-gray-placeholder mb-4">
            Select a template above to preview what will be applied.
          </p>
        )}
        {applyMsg && <p className="text-xs text-palette-green-badge mb-4">{applyMsg}</p>}

        <div className="flex items-center gap-3">
          <Button
            variant="primary"
            size="sm"
            onClick={handleApply}
            disabled={!selectedTemplate || applying}
            className="px-5 py-2.5 rounded-xl font-semibold gap-2"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            {applying ? "Applying…" : "Apply to matching staff"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={!selectedTemplate}
            className="px-5 py-2.5 rounded-xl border border-palette-gray-divider text-palette-slate-heading font-semibold gap-2"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            Preview affected staff
          </Button>
        </div>
      </Card>

      {/* ── Create new template ─────────────────────────────────────────────── */}
      <Card className="shadow-sm border border-palette-gray-subtle" bodyClassName="p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <div className="w-8 h-8 rounded-xl bg-palette-blue-muted flex items-center justify-center shrink-0">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              className="text-palette-indigo-badge"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </div>
          <h2 className="text-base font-semibold text-palette-slate-text">Create new template</h2>
        </div>

        {/* Template name */}
        <div className="mb-5">
          <label className="text-xs font-semibold text-palette-gray-secondary uppercase tracking-widest mb-2 block">
            Template name
          </label>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Morning shift Mon–Fri"
            className="w-full rounded-xl border border-palette-gray-divider px-4 py-3 text-sm text-palette-slate-heading bg-palette-gray-soft outline-none focus:ring-2 focus:ring-palette-blue-ring focus:border-palette-blue-indicator focus:bg-white"
          />
        </div>

        {/* Default start / end */}
        <div className="mb-5">
          <label className="text-xs font-semibold text-palette-gray-secondary uppercase tracking-widest mb-2 block">
            Default start / end
          </label>
          <div className="flex items-center gap-3">
            <select
              value={newStart}
              onChange={(e) => setNewStart(e.target.value)}
              className="flex-1 rounded-xl border border-palette-gray-divider px-4 py-3 text-sm text-palette-slate-heading bg-white outline-none focus:ring-2 focus:ring-palette-blue-ring"
            >
              {SCHEDULE_TIMES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <span className="text-palette-gray-placeholder font-medium">—</span>
            <select
              value={newEnd}
              onChange={(e) => setNewEnd(e.target.value)}
              className="flex-1 rounded-xl border border-palette-gray-divider px-4 py-3 text-sm text-palette-slate-heading bg-white outline-none focus:ring-2 focus:ring-palette-blue-ring"
            >
              {SCHEDULE_TIMES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Working days */}
        <div className="mb-6">
          <label className="text-xs font-semibold text-palette-gray-secondary uppercase tracking-widest mb-3 block">
            Working days
          </label>
          <div className="flex items-center gap-2 flex-wrap">
            {DAY_BUTTONS.map(({ key, label }) => {
              const active = newDays.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleNewDay(key)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                    active
                      ? "bg-palette-blue-badge text-white"
                      : "border border-palette-gray-divider bg-white text-palette-slate-badge hover:border-palette-gray-border hover:bg-palette-gray-soft"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <Button
          variant="primary"
          size="sm"
          onClick={handleSaveTemplate}
          disabled={!newName.trim() || newDays.length === 0}
          className="px-5 py-2.5 rounded-xl font-semibold"
        >
          + Save template
        </Button>
      </Card>
    </div>
  );
}

// ── Copy schedule tab ──────────────────────────────────────────────────────────

function CopyScheduleTab({ roster, clinicId }) {
  // ── Section 1: Copy schedule from another week ──────────────────────────
  const prevMonday = (() => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay() - 6);
    return d.toISOString().split("T")[0];
  })();
  const thisMonday = (() => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay() + 1);
    return d.toISOString().split("T")[0];
  })();

  const [copyFrom, setCopyFrom] = useState(prevMonday);
  const [copyTo, setCopyTo] = useState(thisMonday);
  const [copyApplyTo, setCopyApplyTo] = useState("all");
  const [skipPTO, setSkipPTO] = useState(true);
  const [overwrite, setOverwrite] = useState(false);
  const [copyMsg, setCopyMsg] = useState("");
  const [copyError, setCopyError] = useState("");
  const [preview, setPreview] = useState(null);

  const copySchedule = useCopySchedule();

  // Clear any previous result whenever the user changes the form so they never
  // see a preview or success message that belongs to a different set of inputs.
  function resetResult() {
    setPreview(null);
    setCopyMsg("");
    setCopyError("");
  }

  // Shared call helper — dry_run=true returns a preview without saving anything.
  // Returns the response object on success, null on validation failure or API error.
  async function _callCopy(dry_run) {
    if (!clinicId) {
      setCopyError("No clinic selected.");
      return null;
    }
    if (!copyFrom || !copyTo) {
      setCopyError("Please select both a source and target week.");
      return null;
    }
    setCopyError("");
    setCopyMsg("");
    try {
      return await copySchedule.mutateAsync({
        clinic: clinicId,
        copy_from_week: copyFrom,
        copy_to_week: copyTo,
        apply_to: copyApplyTo,
        skip_pto: skipPTO,
        overwrite,
        dry_run,
      });
    } catch (err) {
      setCopyError(err?.response?.data?.detail || "Failed to copy schedule.");
      return null;
    }
  }

  // "Preview changes" — shows what would be created/skipped without writing.
  async function handlePreview() {
    setPreview(null);
    const result = await _callCopy(true);
    if (result) setPreview(result);
  }

  // "Copy schedule" — commits the overrides; shows a human-readable summary.
  async function handleCopy() {
    setPreview(null);
    const result = await _callCopy(false);
    if (!result) return;
    const s = result.summary;
    if (s.overrides_created === 0) {
      // Nothing was written — give the user a reason rather than a misleading "Done".
      const reasons = [];
      if (s.days_skipped_pto > 0)
        reasons.push(
          `${s.days_skipped_pto} day${s.days_skipped_pto !== 1 ? "s" : ""} covered by PTO`,
        );
      if (s.days_skipped_existing > 0)
        reasons.push(
          `${s.days_skipped_existing} day${s.days_skipped_existing !== 1 ? "s" : ""} with existing overrides`,
        );
      const why = reasons.length ? ` (${reasons.join(", ")})` : "";
      setCopyError(`Nothing to copy — no schedules found or all days were skipped${why}.`);
    } else {
      showSuccess(
        `Schedule copied — ${s.overrides_created} override${s.overrides_created !== 1 ? "s" : ""} created` +
          ` for ${s.practitioners_affected} practitioner${s.practitioners_affected !== 1 ? "s" : ""}.`,
      );
      setCopyMsg("");
    }
  }

  // ── Section 2: Quick override — single day ──────────────────────────────
  const todayStr = new Date().toISOString().split("T")[0];

  // Stores the Practitioner record ID (clinic-assignment PK), not profile_id.
  const [ovProfileId, setOvProfileId] = useState("");
  const [ovDate, setOvDate] = useState(todayStr);
  const [ovType, setOvType] = useState("custom");
  const [ovStart, setOvStart] = useState("8:00 AM");
  const [ovEnd, setOvEnd] = useState("5:00 PM");
  const [ovNote, setOvNote] = useState("");
  const [ovMsg, setOvMsg] = useState("");

  const createOverride = useCreatePractitionerScheduleOverride();

  async function handleSaveOverride() {
    if (!ovProfileId || !ovDate) {
      setOvMsg("Please select an employee and date.");
      return;
    }
    setOvMsg("");
    try {
      await createOverride.mutateAsync({
        practitioner: Number(ovProfileId), // Practitioner PK (clinic-assignment), not profile_id
        date_ranges: [{ start_date: ovDate, end_date: ovDate }],
        is_open: ovType === "custom",
        start_time: ovType === "custom" ? ovStart : "",
        end_time: ovType === "custom" ? ovEnd : "",
        reason: ovNote.trim() || null,
      });
      showSuccess("Override saved successfully.");
      setOvMsg("");
      setOvNote("");
    } catch {
      setOvMsg("Failed to save override.");
    }
  }

  const fieldCls =
    "w-full rounded-xl border border-palette-gray-divider bg-palette-gray-soft px-4 py-3 text-sm text-palette-slate-heading outline-none focus:ring-2 focus:ring-palette-blue-ring focus:border-palette-blue-indicator focus:bg-white";

  return (
    <div className="space-y-5">
      {/* ── Copy schedule from another week ───────────────────────────────── */}
      <Card className="shadow-sm border border-palette-gray-subtle" bodyClassName="p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-8 h-8 rounded-xl bg-palette-blue-muted flex items-center justify-center shrink-0">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              className="text-palette-indigo-badge"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
          </div>
          <h2 className="text-base font-semibold text-palette-slate-text">
            Copy schedule from another week
          </h2>
        </div>

        {/* From / To */}
        <div className="grid grid-cols-2 gap-4 mb-5">
          <div>
            <label className="text-xs font-semibold text-palette-gray-secondary uppercase tracking-widest mb-2 block">
              Copy from week
            </label>
            <input
              type="date"
              value={copyFrom}
              onChange={(e) => {
                setCopyFrom(e.target.value);
                resetResult();
              }}
              className={fieldCls}
            />
            <p className="text-xs text-palette-gray-placeholder mt-1.5">Week starting this date</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-palette-gray-secondary uppercase tracking-widest mb-2 block">
              Copy to week
            </label>
            <input
              type="date"
              value={copyTo}
              onChange={(e) => {
                setCopyTo(e.target.value);
                resetResult();
              }}
              className={fieldCls}
            />
          </div>
        </div>

        {/* Apply to */}
        <div className="mb-5">
          <label className="text-xs font-semibold text-palette-gray-secondary uppercase tracking-widest mb-2 block">
            Apply to
          </label>
          <select
            value={copyApplyTo}
            onChange={(e) => {
              setCopyApplyTo(e.target.value);
              resetResult();
            }}
            className={fieldCls}
          >
            <option value="all">All staff</option>
            {Object.entries(TYPE_LABELS_PLURAL).map(([code, label]) => (
              <option key={code} value={code.toLowerCase()}>
                {label} only
              </option>
            ))}
          </select>
        </div>

        {/* Checkboxes */}
        <div className="flex items-center gap-8 mb-5">
          <CheckboxField
            label="Skip days already marked PTO / leave"
            checked={skipPTO}
            onChange={(v) => {
              setSkipPTO(v);
              resetResult();
            }}
            className="text-palette-slate-heading"
          />
          <CheckboxField
            label="Overwrite existing shifts"
            checked={overwrite}
            onChange={(v) => {
              setOverwrite(v);
              resetResult();
            }}
            className="text-palette-slate-heading"
          />
        </div>

        {/* ── Status messages ───────────────────────────────────────────── */}
        {copyError && <p className="text-xs text-palette-red-label mb-4">{copyError}</p>}
        {copyMsg && <p className="text-xs text-palette-green-badge mb-4">{copyMsg}</p>}

        {/* ── Preview results ───────────────────────────────────────────── */}
        {preview && (
          <div className="mb-4 rounded-xl border border-palette-blue-muted bg-palette-blue-soft p-4">
            <p className="text-xs font-semibold text-palette-blue-heading uppercase tracking-widest mb-3">
              Preview — no changes saved yet
            </p>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="rounded-lg bg-white border border-palette-blue-muted px-3 py-2">
                <p className="text-lg font-bold text-palette-blue-heading leading-none">
                  {preview.summary.overrides_created}
                </p>
                <p className="text-xs text-palette-slate-secondary mt-1">overrides to create</p>
              </div>
              <div className="rounded-lg bg-white border border-palette-blue-muted px-3 py-2">
                <p className="text-lg font-bold text-palette-slate-heading leading-none">
                  {preview.summary.practitioners_affected}
                </p>
                <p className="text-xs text-palette-slate-secondary mt-1">practitioners affected</p>
              </div>
              {preview.summary.days_skipped_pto > 0 && (
                <div className="rounded-lg bg-white border border-palette-amber-muted px-3 py-2">
                  <p className="text-lg font-bold text-palette-amber-badge leading-none">
                    {preview.summary.days_skipped_pto}
                  </p>
                  <p className="text-xs text-palette-slate-secondary mt-1">days skipped (PTO)</p>
                </div>
              )}
              {preview.summary.days_skipped_existing > 0 && (
                <div className="rounded-lg bg-white border border-palette-gray-divider px-3 py-2">
                  <p className="text-lg font-bold text-palette-slate-secondary leading-none">
                    {preview.summary.days_skipped_existing}
                  </p>
                  <p className="text-xs text-palette-slate-secondary mt-1">
                    days skipped (existing override)
                  </p>
                </div>
              )}
            </div>
            {preview.summary.overrides_created === 0 && (
              <p className="text-xs text-palette-slate-secondary">
                Nothing to copy — all days are either already overridden or covered by PTO.
              </p>
            )}
          </div>
        )}

        <div className="flex items-center gap-3">
          <Button
            variant="primary"
            size="sm"
            onClick={handleCopy}
            disabled={copySchedule.isPending}
            className="px-5 py-2.5 rounded-xl font-semibold gap-2"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
            {copySchedule.isPending ? "Copying…" : "Copy schedule"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePreview}
            disabled={copySchedule.isPending}
            className="px-5 py-2.5 rounded-xl border border-palette-gray-divider text-palette-slate-heading font-semibold gap-2"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            {copySchedule.isPending ? "Loading…" : "Preview changes"}
          </Button>
        </div>
      </Card>

      {/* ── Quick override — single day ────────────────────────────────────── */}
      <Card className="shadow-sm border border-palette-gray-subtle" bodyClassName="p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-8 h-8 rounded-xl bg-palette-blue-muted flex items-center justify-center shrink-0">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              className="text-palette-indigo-badge"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
              <circle cx="12" cy="16" r="2" />
            </svg>
          </div>
          <h2 className="text-base font-semibold text-palette-slate-text">
            Quick override — single day
          </h2>
        </div>

        {/* Employee / Date / Override type */}
        <div className="grid grid-cols-3 gap-4 mb-5">
          <div>
            <label className="text-xs font-semibold text-palette-gray-secondary uppercase tracking-widest mb-2 block">
              Employee
            </label>
            <select
              value={ovProfileId}
              onChange={(e) => setOvProfileId(e.target.value)}
              className={fieldCls}
            >
              <option value="">Select employee…</option>
              {roster.map((p) => (
                <option key={p.id} value={p.id}>
                  {safeText(p.name)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-palette-gray-secondary uppercase tracking-widest mb-2 block">
              Date
            </label>
            <input
              type="date"
              value={ovDate}
              onChange={(e) => setOvDate(e.target.value)}
              className={fieldCls}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-palette-gray-secondary uppercase tracking-widest mb-2 block">
              Override type
            </label>
            <select value={ovType} onChange={(e) => setOvType(e.target.value)} className={fieldCls}>
              <option value="custom">Custom shift</option>
              <option value="off">Day off</option>
            </select>
          </div>
        </div>

        {/* Start / End times — only for custom shift */}
        {ovType === "custom" && (
          <div className="grid grid-cols-2 gap-4 mb-5">
            <div>
              <label className="text-xs font-semibold text-palette-gray-secondary uppercase tracking-widest mb-2 block">
                Start time
              </label>
              <select
                value={ovStart}
                onChange={(e) => setOvStart(e.target.value)}
                className={fieldCls}
              >
                {SCHEDULE_TIMES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-palette-gray-secondary uppercase tracking-widest mb-2 block">
                End time
              </label>
              <select value={ovEnd} onChange={(e) => setOvEnd(e.target.value)} className={fieldCls}>
                {SCHEDULE_TIMES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Note */}
        <div className="mb-5">
          <label className="text-xs font-semibold text-palette-gray-secondary uppercase tracking-widest mb-2 block">
            Note (optional)
          </label>
          <textarea
            value={ovNote}
            onChange={(e) => setOvNote(e.target.value)}
            placeholder="e.g. Covering for Rosa, leaving at 3 PM"
            rows={3}
            className="w-full resize-none rounded-xl border border-palette-gray-divider bg-palette-gray-soft px-4 py-3 text-sm text-palette-slate-heading outline-none focus:ring-2 focus:ring-palette-blue-ring focus:border-palette-blue-indicator focus:bg-white"
          />
        </div>

        {ovMsg && (
          <p
            className={`text-xs mb-4 ${ovMsg.startsWith("Failed") ? "text-palette-red-label" : "text-palette-green-badge"}`}
          >
            {ovMsg}
          </p>
        )}

        <Button
          variant="primary"
          size="sm"
          onClick={handleSaveOverride}
          disabled={createOverride.isPending}
          className="px-5 py-2.5 rounded-xl font-semibold gap-2"
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          {createOverride.isPending ? "Saving…" : "Save override"}
        </Button>
      </Card>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

const TABS = [
  { key: "single", label: "Single employee", Icon: IconSingleEmployee },
  { key: "bulk", label: "Bulk / week template", Icon: IconBulk },
  { key: "copy", label: "Copy schedule", Icon: IconCopy },
];

export default function NewShiftPage() {
  const { clinicId } = useParams();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("single");
  const [search, setSearch] = useState("");

  // Weekly-schedule rows for the whole clinic — the editor reads base rows from here and
  // writes back through the same table.
  const { data: weeklyScheduleData } = usePractitionerWeeklySchedules({ clinic: clinicId });
  const schedulesByPractitioner = useMemo(
    () => groupSchedulesByPractitioner(weeklyScheduleData),
    [weeklyScheduleData],
  );

  // ── Single-employee schedule editor state ─────────────────────────────────
  const [expandedId, setExpandedId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [schedSaving, setSchedSaving] = useState(false);
  const [schedMsg, setSchedMsg] = useState({});
  // { practitionerId: { from, until } } — both blank writes the base row; both set
  // writes a date-bounded row that overrides the base one inside that range.
  const [effectiveDates, setEffectiveDates] = useState({});
  const schedSaveMut = useSavePractitionerWeeklySchedule();

  // All break edits funnel through here so the draft's day slot is always replaced, never
  // mutated — the breaks array is shared with the query cache otherwise.
  function setDayBreaks(pid, dayKey, updater) {
    setDrafts((prev) => {
      const day = prev[pid]?.[dayKey] ?? { start: "", end: "" };
      return {
        ...prev,
        [pid]: {
          ...prev[pid],
          [dayKey]: { ...day, breaks: updater(slotBreaks(day)) },
        },
      };
    });
  }

  function openEditor(p) {
    // Toggle: clicking the same row again collapses it
    if (expandedId === p.id) {
      setExpandedId(null);
      return;
    }
    // Seed from the recurring default. The effective-date inputs decide where the save
    // lands, so the editor always opens on the base schedule regardless of dated rows.
    const existing = baseSchedule(schedulesByPractitioner[p.id]);
    setDrafts((prev) => ({
      ...prev,
      [p.id]: Object.fromEntries(
        DAY_INDICATORS.map(({ key }) => [
          key,
          {
            start: "",
            end: "",
            type: "regular",
            ...existing[key],
            breaks: slotBreaks(existing[key]).map((br) => ({ ...br })),
          },
        ]),
      ),
    }));
    setExpandedId(p.id);
    setSchedMsg((prev) => ({ ...prev, [p.id]: "" }));
  }

  // Inline message + toast together: the inline line keeps the failure next to the
  // field it is about, the toast makes sure a blocked save is never silent.
  function fail(p, message) {
    setSchedMsg((prev) => ({ ...prev, [p.id]: message }));
    showError(message);
  }

  async function saveSchedule(p) {
    const name = safeText(p?.name) || "this employee";
    setSchedSaving(true);
    setSchedMsg((prev) => ({ ...prev, [p.id]: "" }));
    const draft = drafts[p.id] || {};
    // compactSchedule keeps complete days plus their complete breaks; default the shift
    // type the way this editor always has.
    const toSave = compactSchedule(
      Object.fromEntries(
        DAY_INDICATORS.map(({ key }) => [
          key,
          draft[key] ? { ...draft[key], type: draft[key].type || "regular" } : undefined,
        ]),
      ),
      DAY_INDICATORS.map(({ key }) => key),
    );
    const { from, until } = effectiveDates[p.id] ?? { from: "", until: "" };
    // The range is all-or-nothing on the backend; catch it here so the user gets a
    // pointed message instead of a 400.
    if (!!from !== !!until) {
      fail(p, "Set both effective dates, or leave both blank to save the default schedule.");
      setSchedSaving(false);
      return;
    }
    if (from && until && until < from) {
      fail(p, "Effective until cannot be before effective from.");
      setSchedSaving(false);
      return;
    }
    try {
      await schedSaveMut.mutateAsync({
        practitionerId: p.id,
        schedule: toSave,
        effectiveStart: from || null,
        effectiveEnd: until || null,
        existingRows: schedulesByPractitioner[p.id] ?? [],
      });
      setSchedMsg((prev) => ({
        ...prev,
        [p.id]: from ? `✓ Saved for ${from} → ${until}` : "✓ Saved",
      }));
      // The inline "✓ Saved" lives INSIDE the editor card, which this collapses —
      // so on its own the save looked like nothing happened. The toast is the
      // part the user actually sees.
      showSuccess(
        from ? `Schedule saved for ${name} — ${from} → ${until}.` : `Schedule saved for ${name}.`,
      );
      setExpandedId(null);
    } catch (e) {
      // A cross-clinic clash comes back as a 400 on `schedule`, a bad date window on
      // `effective_range`; extractApiError unwraps whichever DRF sent.
      const msg = extractApiError(e, "Failed to save — check for schedule conflicts.");
      setSchedMsg((prev) => ({ ...prev, [p.id]: msg }));
      showError(msg);
    } finally {
      setSchedSaving(false);
    }
  }

  const { data: practitionersData, isLoading } = usePractitioners({ clinic: clinicId });

  const roster = useMemo(() => {
    return getRoster(practitionersData)
      .filter((p) => safeText(p?.name) && safeText(p?.status).toLowerCase() !== "inactive")
      .slice()
      .sort((a, b) => {
        const tA = TYPE_ORDER[normalizeTypeForUi(a?.practitioner_type)] ?? 99;
        const tB = TYPE_ORDER[normalizeTypeForUi(b?.practitioner_type)] ?? 99;
        return tA !== tB ? tA - tB : safeText(a?.name).localeCompare(safeText(b?.name));
      });
  }, [practitionersData]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roster;
    return roster.filter((p) => {
      const name = safeText(p?.name).toLowerCase();
      const role = (TYPE_LABELS[normalizeTypeForUi(p?.practitioner_type)] ?? "").toLowerCase();
      return name.includes(q) || role.includes(q);
    });
  }, [roster, search]);

  return (
    <div className="min-h-screen bg-palette-gray-soft">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-palette-gray-divider px-6 py-4">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-palette-blue-muted flex items-center justify-center shrink-0">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                className="text-palette-indigo-badge"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </div>
            <div>
              <h1 className="text-lg font-bold text-palette-slate-text">Schedule entry</h1>
              <p className="text-xs text-palette-gray-secondary">Create and edit staff schedules</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/report/${clinicId}/schedule`)}
            className="rounded-full border border-palette-gray-divider text-palette-slate-heading px-4 py-2"
          >
            ‹ Back to schedule
          </Button>
        </div>
      </div>

      {/* ── Tab navigation ──────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-palette-gray-divider px-6">
        <div className="flex w-full">
          {TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === key
                  ? "border-palette-blue-badge text-palette-blue-heading"
                  : "border-transparent text-palette-gray-secondary hover:text-palette-slate-heading"
              }`}
            >
              <Icon />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────────────────────── */}
      <div className="px-6 py-6 w-full">
        {/* ── Single employee tab ──────────────────────────────────────────── */}
        {activeTab === "single" && (
          <div className="bg-white rounded-2xl border border-palette-gray-subtle shadow-sm overflow-hidden">
            {/* Section header */}
            <div className="flex items-center gap-3 px-6 py-5 border-b border-palette-gray-subtle">
              <div className="w-8 h-8 rounded-full bg-palette-blue-muted flex items-center justify-center text-palette-blue-badge shrink-0">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              <h2 className="text-base font-semibold text-palette-slate-text">Select employee</h2>
            </div>

            {/* Search */}
            <div className="px-6 py-4 border-b border-palette-gray-subtle">
              <div className="relative">
                <svg
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-palette-gray-placeholder"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or role..."
                  className="w-full pl-11 pr-4 py-3 rounded-xl bg-palette-gray-soft border border-palette-gray-divider text-sm text-palette-slate-heading outline-none focus:ring-2 focus:ring-palette-blue-ring focus:border-palette-blue-indicator"
                />
              </div>
            </div>

            {/* Practitioner list */}
            {isLoading ? (
              <div className="py-12 text-center text-sm text-palette-gray-placeholder">
                Loading staff…
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-12 text-center text-sm text-palette-gray-placeholder">
                No staff found.
              </div>
            ) : (
              <div>
                {filtered.map((p, i) => {
                  const name = safeText(p?.name) || "—";
                  const type = normalizeTypeForUi(p?.practitioner_type);
                  const schedule = baseSchedule(schedulesByPractitioner[p.id]);
                  const isLast = i === filtered.length - 1;

                  const isExpanded = expandedId === p.id;
                  const draft = drafts[p.id] ?? {};
                  const msg = schedMsg[p.id] ?? "";

                  return (
                    <div
                      key={p.id}
                      className={!isLast ? "border-b border-palette-gray-subtle" : ""}
                    >
                      {/* ── Practitioner row ─────────────────────────────────── */}
                      <div
                        className={`flex items-center gap-4 px-6 py-4 cursor-pointer transition-colors ${
                          isExpanded ? "bg-palette-blue-soft/40" : "hover:bg-palette-gray-soft/60"
                        }`}
                        onClick={() => openEditor(p)}
                      >
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${typeColor(type)}`}
                        >
                          {getInitials(name)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-palette-slate-text leading-snug">
                            {name}
                          </p>
                          <span
                            className={`inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-semibold ${typeColor(type)}`}
                          >
                            {TYPE_LABELS[type] ?? type}
                          </span>
                        </div>
                        {/* Day indicators */}
                        <div className="flex items-center gap-1.5 shrink-0">
                          {DAY_INDICATORS.map(({ key, letter }, idx) => {
                            const hasShift = !!(schedule[key]?.start && schedule[key]?.end);
                            return (
                              <span
                                key={`${key}-${idx}`}
                                className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold transition-colors ${
                                  hasShift
                                    ? "bg-palette-blue-badge text-white"
                                    : "bg-palette-gray-subtle text-palette-gray-border"
                                }`}
                              >
                                {letter}
                              </span>
                            );
                          })}
                        </div>
                        {/* Expand/collapse chevron */}
                        <span
                          className={`text-palette-gray-placeholder text-sm ml-1 shrink-0 transition-transform duration-150 ${isExpanded ? "rotate-90" : ""}`}
                        >
                          ›
                        </span>
                      </div>

                      {/* ── Schedule editor card ─────────────────────────────── */}
                      {isExpanded &&
                        (() => {
                          const effDates = effectiveDates[p.id] ?? { from: "", until: "" };
                          const weeklyHrs = DAY_INDICATORS.reduce((acc, { key }) => {
                            const slot = draft[key] ?? {};
                            return acc + (slotHours(slot) ?? 0);
                          }, 0);
                          // First bad break across the week — blocks Save so an inverted or
                          // out-of-range break is caught here instead of as a 400.
                          const draftBreakErr = scheduleBreakError(
                            draft,
                            DAY_INDICATORS.map(({ key }) => key),
                          );

                          return (
                            <div className="mx-4 mb-4 mt-1 rounded-2xl border border-palette-gray-divider bg-white shadow-sm overflow-hidden">
                              {/* Card header */}
                              <div className="flex items-center gap-3 px-5 py-4 border-b border-palette-gray-subtle">
                                <div
                                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${typeColor(type)}`}
                                >
                                  {getInitials(name)}
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-palette-slate-text">
                                    Weekly schedule — {name}
                                  </p>
                                  <span
                                    className={`inline-flex items-center mt-0.5 px-2 py-0.5 rounded-full text-xs font-semibold ${typeColor(type)}`}
                                  >
                                    {TYPE_LABELS[type] ?? type}
                                  </span>
                                </div>
                              </div>

                              {/* Effective dates — blank/blank saves the recurring default;
                                  both set saves a row that overrides it in that range only. */}
                              <div className="grid grid-cols-2 gap-4 px-5 pt-4 border-b-0 bg-palette-gray-soft/40">
                                {[
                                  ["from", "Effective from"],
                                  ["until", "Effective until"],
                                ].map(([field, label]) => (
                                  <div key={field}>
                                    <label className="text-xs font-semibold text-palette-gray-secondary uppercase tracking-widest mb-1.5 block">
                                      {label}
                                    </label>
                                    <input
                                      type="date"
                                      value={effDates[field]}
                                      onChange={(e) =>
                                        setEffectiveDates((prev) => ({
                                          ...prev,
                                          [p.id]: { ...effDates, [field]: e.target.value },
                                        }))
                                      }
                                      className="w-full rounded-xl border border-palette-gray-divider bg-white px-3 py-2 text-sm text-palette-slate-heading outline-none focus:ring-2 focus:ring-palette-blue-ring"
                                    />
                                  </div>
                                ))}
                              </div>
                              <p className="px-5 pb-4 pt-2 text-xs text-palette-gray-secondary border-b border-palette-gray-subtle bg-palette-gray-soft/40">
                                {effDates.from && effDates.until
                                  ? `These hours apply from ${effDates.from} to ${effDates.until} only, overriding the default schedule for those dates.`
                                  : "Leave both blank to save the recurring default schedule. Set both to save hours that apply only in that date range."}
                              </p>

                              {/* Overlap warning. Deliberately NOT per-practitioner. The
                                  backend exempts a hardcoded list of generic shared-coverage
                                  profiles (temp hygiene, associate dentist, and the like) from
                                  the cross-clinic check, and that list is server-side only — it
                                  is not in any API response, and an exempted save comes back as
                                  a plain 200 with no marker on it. So this page has no way to
                                  tell a shared profile from a real person, and the copy has to
                                  hold for both rather than branch on something we cannot know.
                                  If it ever needs to branch, the flag has to be exposed first —
                                  that is a backend decision that was already reversed once. */}
                              <div className="mx-5 mt-3 flex items-start gap-2 text-xs text-palette-amber-heading bg-palette-amber-soft border border-palette-amber-border rounded-xl px-3 py-2">
                                <span className="shrink-0 mt-0.5">⚠️</span>
                                <span>
                                  Hours that overlap the same employee at another clinic are
                                  rejected on save, and the error names the clinic and the times.
                                  Generic shared-coverage profiles are exempt from that check, so
                                  overlapping hours on those save normally.
                                </span>
                              </div>

                              {/* Day table */}
                              <div className="mt-3">
                                <table className="w-full">
                                  <thead>
                                    <tr className="border-b border-palette-gray-subtle">
                                      {[
                                        "Day",
                                        "On",
                                        "Shift Type",
                                        "Start",
                                        "End",
                                        "Break",
                                        "Hours",
                                        "Coverage",
                                      ].map((h) => (
                                        <th
                                          key={h}
                                          className="px-5 py-2.5 text-left text-xs font-semibold text-palette-gray-placeholder uppercase tracking-wide first:w-20"
                                        >
                                          {h}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {DAY_INDICATORS.map(({ key }) => {
                                      const slot = draft[key] ?? { start: "", end: "" };
                                      const isOn = !!(slot.start && slot.end);
                                      // Net of breaks: a day with a lunch break must not read an hour longer than it is.
                                      const hrs = slotHours(slot);
                                      const breaks = slotBreaks(slot);
                                      const dayBreakErr = breakError(slot);
                                      return (
                                        <tr
                                          key={key}
                                          className="border-b border-palette-gray-soft last:border-0 hover:bg-palette-gray-soft/40"
                                        >
                                          {/* Day name */}
                                          <td className="px-5 py-3 text-sm font-semibold text-palette-slate-heading">
                                            {DAY_SHORT[key]}
                                          </td>

                                          {/* iOS-style toggle */}
                                          <td className="px-5 py-3">
                                            <button
                                              type="button"
                                              onClick={() =>
                                                setDrafts((prev) => ({
                                                  ...prev,
                                                  [p.id]: {
                                                    ...prev[p.id],
                                                    [key]: isOn
                                                      ? { start: "", end: "", type: "regular" }
                                                      : {
                                                          start: "8:00 AM",
                                                          end: "5:00 PM",
                                                          type: "regular",
                                                        },
                                                  },
                                                }))
                                              }
                                              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${isOn ? "bg-palette-blue-badge" : "bg-palette-gray-divider"}`}
                                            >
                                              <span
                                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${isOn ? "translate-x-5" : "translate-x-0"}`}
                                              />
                                            </button>
                                          </td>

                                          {/* Shift type */}
                                          <td className="px-5 py-3">
                                            {isOn ? (
                                              <div className="flex items-center gap-1 flex-wrap">
                                                {["Regular", "On-call", "Training", "PTO"].map(
                                                  (t) => {
                                                    const tKey = t.toLowerCase().replace("-", "_");
                                                    const active =
                                                      (slot.type || "regular") === tKey;
                                                    return (
                                                      <button
                                                        key={t}
                                                        type="button"
                                                        onClick={() => {
                                                          if (t === "PTO") {
                                                            // PTO = mark off
                                                            setDrafts((prev) => ({
                                                              ...prev,
                                                              [p.id]: {
                                                                ...prev[p.id],
                                                                [key]: {
                                                                  start: "",
                                                                  end: "",
                                                                  type: "pto",
                                                                },
                                                              },
                                                            }));
                                                          } else {
                                                            setDrafts((prev) => ({
                                                              ...prev,
                                                              [p.id]: {
                                                                ...prev[p.id],
                                                                [key]: { ...slot, type: tKey },
                                                              },
                                                            }));
                                                          }
                                                        }}
                                                        className={`px-2 py-0.5 rounded-full text-xs font-semibold border transition-colors ${
                                                          active
                                                            ? "bg-palette-blue-muted text-palette-blue-heading border-palette-blue-border"
                                                            : "bg-white text-palette-gray-secondary border-palette-gray-divider hover:bg-palette-gray-soft"
                                                        }`}
                                                      >
                                                        {t}
                                                      </button>
                                                    );
                                                  },
                                                )}
                                              </div>
                                            ) : (
                                              <span className="text-palette-gray-border text-sm">
                                                —
                                              </span>
                                            )}
                                          </td>

                                          {/* Start time */}
                                          <td className="px-5 py-3">
                                            {isOn ? (
                                              <select
                                                value={slot.start}
                                                onChange={(e) =>
                                                  setDrafts((prev) => ({
                                                    ...prev,
                                                    [p.id]: {
                                                      ...prev[p.id],
                                                      [key]: { ...slot, start: e.target.value },
                                                    },
                                                  }))
                                                }
                                                className="rounded-xl border border-palette-gray-divider bg-palette-gray-soft px-3 py-1.5 text-sm text-palette-slate-heading outline-none focus:ring-2 focus:ring-palette-blue-ring"
                                              >
                                                {SCHEDULE_TIMES.map((t) => (
                                                  <option key={t} value={t}>
                                                    {t}
                                                  </option>
                                                ))}
                                              </select>
                                            ) : (
                                              <span className="text-palette-gray-border text-sm">
                                                —
                                              </span>
                                            )}
                                          </td>

                                          {/* End time */}
                                          <td className="px-5 py-3">
                                            {isOn ? (
                                              <select
                                                value={slot.end}
                                                onChange={(e) =>
                                                  setDrafts((prev) => ({
                                                    ...prev,
                                                    [p.id]: {
                                                      ...prev[p.id],
                                                      [key]: { ...slot, end: e.target.value },
                                                    },
                                                  }))
                                                }
                                                className="rounded-xl border border-palette-gray-divider bg-palette-gray-soft px-3 py-1.5 text-sm text-palette-slate-heading outline-none focus:ring-2 focus:ring-palette-blue-ring"
                                              >
                                                {SCHEDULE_TIMES.map((t) => (
                                                  <option key={t} value={t}>
                                                    {t}
                                                  </option>
                                                ))}
                                              </select>
                                            ) : (
                                              <span className="text-palette-gray-border text-sm">
                                                —
                                              </span>
                                            )}
                                          </td>

                                          {/* Break — one From–To pair per break, laid out
                                                inline with the Add button; a second break
                                                wraps onto the next line. Add appends an empty
                                                break, which renders its own pickers. Only for
                                                days that are on, since a break sits inside
                                                the shift. */}
                                          <td className="px-5 py-3 align-top">
                                            {isOn ? (
                                              <div className="flex flex-wrap items-center gap-2">
                                                {breaks.map((br, idx) => (
                                                  <div
                                                    key={idx}
                                                    className="flex items-center gap-1"
                                                  >
                                                    <select
                                                      value={br.start || ""}
                                                      onChange={(e) =>
                                                        setDayBreaks(p.id, key, (list) =>
                                                          list.map((b, i) =>
                                                            i === idx
                                                              ? { ...b, start: e.target.value }
                                                              : b,
                                                          ),
                                                        )
                                                      }
                                                      className={`rounded-lg border bg-white px-2 py-1 text-xs text-palette-slate-heading outline-none focus:ring-2 focus:ring-palette-blue-ring ${
                                                        dayBreakErr
                                                          ? "border-palette-red-indicator"
                                                          : "border-palette-gray-divider"
                                                      }`}
                                                    >
                                                      <option value="">From</option>
                                                      {SCHEDULE_TIMES.map((t) => (
                                                        <option key={t} value={t}>
                                                          {t}
                                                        </option>
                                                      ))}
                                                    </select>
                                                    <span className="text-xs text-palette-gray-placeholder">
                                                      –
                                                    </span>
                                                    <select
                                                      value={br.end || ""}
                                                      onChange={(e) =>
                                                        setDayBreaks(p.id, key, (list) =>
                                                          list.map((b, i) =>
                                                            i === idx
                                                              ? { ...b, end: e.target.value }
                                                              : b,
                                                          ),
                                                        )
                                                      }
                                                      className={`rounded-lg border bg-white px-2 py-1 text-xs text-palette-slate-heading outline-none focus:ring-2 focus:ring-palette-blue-ring ${
                                                        dayBreakErr
                                                          ? "border-palette-red-indicator"
                                                          : "border-palette-gray-divider"
                                                      }`}
                                                    >
                                                      <option value="">To</option>
                                                      {SCHEDULE_TIMES.map((t) => (
                                                        <option key={t} value={t}>
                                                          {t}
                                                        </option>
                                                      ))}
                                                    </select>
                                                    <button
                                                      type="button"
                                                      onClick={() =>
                                                        setDayBreaks(p.id, key, (list) =>
                                                          list.filter((_, i) => i !== idx),
                                                        )
                                                      }
                                                      className="ml-0.5 text-xs text-palette-gray-placeholder hover:text-palette-red-label"
                                                      title="Remove break"
                                                    >
                                                      ✕
                                                    </button>
                                                  </div>
                                                ))}
                                                <button
                                                  type="button"
                                                  title={
                                                    dayBreakErr
                                                      ? "Fix this break before adding another"
                                                      : "Add lunch break"
                                                  }
                                                  disabled={!!dayBreakErr}
                                                  onClick={() =>
                                                    setDayBreaks(p.id, key, (list) => [
                                                      ...list,
                                                      { start: "", end: "" },
                                                    ])
                                                  }
                                                  className={`${BREAK_BTN_CLS} disabled:cursor-not-allowed disabled:opacity-40`}
                                                >
                                                  Add
                                                </button>
                                                {dayBreakErr && (
                                                  <span className="basis-full text-[11px] font-medium text-palette-red-label">
                                                    {dayBreakErr}
                                                  </span>
                                                )}
                                              </div>
                                            ) : (
                                              <span className="text-palette-gray-border text-sm">
                                                —
                                              </span>
                                            )}
                                          </td>

                                          {/* Hours — net of breaks */}
                                          <td className="px-5 py-3 align-top text-sm text-palette-gray-placeholder">
                                            {hrs !== null ? `${hrs}h` : "—"}
                                          </td>

                                          {/* Coverage check — ✓ when scheduled, warning if PTO */}
                                          <td className="px-5 py-3">
                                            {isOn && slot.type !== "pto" ? (
                                              <span
                                                className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-palette-green-muted text-palette-green-badge"
                                                title="Covered"
                                              >
                                                <svg
                                                  width="12"
                                                  height="12"
                                                  viewBox="0 0 24 24"
                                                  fill="none"
                                                  stroke="currentColor"
                                                  strokeWidth="2.5"
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                >
                                                  <polyline points="20 6 9 17 4 12" />
                                                </svg>
                                              </span>
                                            ) : isOn && slot.type === "pto" ? (
                                              <span className="text-xs text-palette-amber-label font-medium">
                                                PTO
                                              </span>
                                            ) : (
                                              <span className="text-palette-gray-border text-sm">
                                                —
                                              </span>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>

                              {/* Footer */}
                              <div className="flex items-center justify-between px-5 py-4 border-t border-palette-gray-subtle bg-palette-gray-soft/40">
                                <span className="text-sm text-palette-gray-secondary">
                                  Weekly total:{" "}
                                  <span className="font-semibold text-palette-slate-heading">
                                    {Math.round(weeklyHrs * 10) / 10} hrs
                                  </span>
                                </span>
                                <div className="flex items-center gap-2">
                                  {msg ? (
                                    <span
                                      className={`text-xs font-medium mr-1 ${msg.startsWith("✓") ? "text-palette-green-badge" : "text-palette-red-label"}`}
                                    >
                                      {msg}
                                    </span>
                                  ) : (
                                    draftBreakErr && (
                                      <span className="mr-1 text-xs font-medium text-palette-red-label">
                                        {DAY_SHORT[draftBreakErr.dayKey]}: {draftBreakErr.message}
                                      </span>
                                    )
                                  )}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setDrafts((prev) => ({
                                        ...prev,
                                        [p.id]: Object.fromEntries(
                                          DAY_INDICATORS.map(({ key }) => [
                                            key,
                                            { start: "", end: "" },
                                          ]),
                                        ),
                                      }))
                                    }
                                    className="px-4 py-2 rounded-xl border border-palette-gray-divider text-sm text-palette-slate-badge hover:bg-palette-gray-subtle transition-colors"
                                  >
                                    Clear
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setExpandedId(null)}
                                    className="px-4 py-2 rounded-xl border border-palette-gray-divider text-sm text-palette-slate-badge hover:bg-palette-gray-subtle transition-colors"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => saveSchedule(p)}
                                    disabled={schedSaving || !!draftBreakErr}
                                    title={draftBreakErr?.message || "Save weekly schedule"}
                                    className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-palette-blue-badge text-sm font-semibold text-white hover:bg-palette-blue-heading disabled:opacity-50 transition-colors"
                                  >
                                    {schedSaving ? "Saving…" : "✓ Save schedule"}
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Bulk / week template tab ─────────────────────────────────────── */}
        {activeTab === "bulk" && <BulkWeekTemplateTab roster={roster} clinicId={clinicId} />}

        {/* ── Copy schedule tab ────────────────────────────────────────────── */}
        {activeTab === "copy" && <CopyScheduleTab roster={roster} clinicId={clinicId} />}
      </div>
    </div>
  );
}
